import { useState, useEffect, useMemo } from 'react';

const CloudAlertSettingsTab = ({
    alertTTL: alertTTLProp,
    acceptRequired,
    finishRequired,
    reasonRequired,
    portalAfr,
    releaseTimeout,
    finishTimeout,
    clearTimeout: clearTimeoutProp,
    areaDeviceTimeout,
    residentDeviceTimeout,
    alertSettingId,
    onSave,
    saving,
}) => {
    const [ttl, setTtl] = useState(alertTTLProp || 60);
    // Combined "Accept, Finish, and Record Reason Workflow" toggle — drives
    // accept_required / finish_required / reason_required together as one unit.
    const [workflow, setWorkflow] = useState(!!acceptRequired && !!finishRequired && !!reasonRequired);
    const [portal, setPortal] = useState(!!portalAfr);
    const [release, setRelease] = useState(releaseTimeout || 1);
    const [finishTime, setFinishTime] = useState(finishTimeout || 5);
    const [clearTime, setClearTime] = useState(clearTimeoutProp || 4);
    const [areaDeviceTO, setAreaDeviceTO] = useState(areaDeviceTimeout ?? 120);
    const [residentDeviceTO, setResidentDeviceTO] = useState(residentDeviceTimeout ?? 120);

    // Sync from props when data reloads (campus switch)
    useEffect(() => {
        setTtl(alertTTLProp || 60);
        setWorkflow(!!acceptRequired && !!finishRequired && !!reasonRequired);
        setPortal(!!portalAfr);
        setRelease(releaseTimeout || 1);
        setFinishTime(finishTimeout || 5);
        setClearTime(clearTimeoutProp || 4);
        setAreaDeviceTO(areaDeviceTimeout ?? 120);
        setResidentDeviceTO(residentDeviceTimeout ?? 120);
    }, [alertTTLProp, acceptRequired, finishRequired, reasonRequired, portalAfr, releaseTimeout, finishTimeout, clearTimeoutProp, areaDeviceTimeout, residentDeviceTimeout]);

    // Clamp slider values when TTL decreases
    useEffect(() => {
        if (release > ttl) setRelease(ttl);
        if (clearTime > ttl - 1) setClearTime(Math.max(4, ttl - 1));
        if (finishTime > ttl) setFinishTime(ttl);
    }, [ttl]);

    // Computed dirty detection — auto-resets when values match originals
    const isDirty = useMemo(() => {
        return (
            ttl !== (alertTTLProp || 60) ||
            workflow !== (!!acceptRequired && !!finishRequired && !!reasonRequired) ||
            portal !== !!portalAfr ||
            release !== (releaseTimeout || 1) ||
            finishTime !== (finishTimeout || 5) ||
            clearTime !== (clearTimeoutProp || 4) ||
            areaDeviceTO !== (areaDeviceTimeout ?? 120) ||
            residentDeviceTO !== (residentDeviceTimeout ?? 120)
        );
    }, [ttl, workflow, portal, release, finishTime, clearTime, areaDeviceTO, residentDeviceTO,
        alertTTLProp, acceptRequired, finishRequired, reasonRequired, portalAfr, releaseTimeout, finishTimeout, clearTimeoutProp,
        areaDeviceTimeout, residentDeviceTimeout]);

    // Single combined toggle — on/off enables or disables the whole
    // accept → finish → record-reason workflow at once.
    const toggleWorkflow = () => {
        setWorkflow((prev) => !prev);
    };

    const togglePortal = () => {
        setPortal((prev) => !prev);
    };

    // Range slider handlers
    const handleClearChange = (val) => {
        const v = parseInt(val);
        setClearTime(v);
        if (v >= finishTime) setFinishTime(v + 1);
    };

    const handleFinishChange = (val) => {
        const v = parseInt(val);
        if (v > clearTime) {
            setFinishTime(v);
        }
    };

    const handleTtlChange = (e) => {
        let v = parseInt(e.target.value);
        if (isNaN(v)) v = 60;
        v = Math.max(60, Math.min(360, v));
        // Round to nearest 5
        v = Math.round(v / 5) * 5;
        setTtl(v);
    };

    const handleSave = () => {
        onSave({
            alertSettingId,
            acceptRequired: workflow,
            finishRequired: workflow,
            reasonRequired: workflow,
            portalAfr: portal,
            releaseTimeout: release,
            finishTimeout: finishTime,
            clearTimeout: clearTime,
            alertTTL: ttl,
            areaDeviceTimeout: areaDeviceTO,
            residentDeviceTimeout: residentDeviceTO,
        });
    };

    const handleCancel = () => {
        setTtl(alertTTLProp || 60);
        setWorkflow(!!acceptRequired && !!finishRequired && !!reasonRequired);
        setPortal(!!portalAfr);
        setRelease(releaseTimeout || 1);
        setFinishTime(finishTimeout || 5);
        setClearTime(clearTimeoutProp || 4);
        setAreaDeviceTO(areaDeviceTimeout ?? 120);
        setResidentDeviceTO(residentDeviceTimeout ?? 120);
    };

    const handleAreaDeviceTOChange = (e) => {
        let v = parseInt(e.target.value);
        if (isNaN(v)) v = 120;
        v = Math.max(120, Math.min(1440, v));
        setAreaDeviceTO(v);
    };

    const handleResidentDeviceTOChange = (e) => {
        let v = parseInt(e.target.value);
        if (isNaN(v)) v = 120;
        v = Math.max(120, Math.min(1440, v));
        setResidentDeviceTO(v);
    };

    // Dynamic slider ranges based on TTL
    const releaseMax = ttl;
    const clearMax = ttl - 1;
    const finishMax = ttl;

    // Slider position helpers
    const releasePercent = releaseMax > 1 ? ((release - 1) / (releaseMax - 1)) * 100 : 0;
    const finishPercent = finishMax > 5 ? ((finishTime - 5) / (finishMax - 5)) * 100 : 0;
    const clearPercent = clearMax > 4 ? ((clearTime - 4) / (clearMax - 4)) * 100 : 0;

    const toggleRows = [
        {
            key: 'workflow',
            title: 'Accept, Finish, and Record Reason Workflow',
            desc: 'Require staff to accept alerts, mark them finished, and record a reason. Enabling turns on all three steps; disabling turns them all off.',
            value: workflow,
            onChange: toggleWorkflow,
            disabled: false,
        },
        {
            key: 'portal',
            title: 'Allow Alert Interactions in Portal',
            desc: 'Lets staff accept, finish, and record reason codes for alerts from the web portal (subject to the interaction settings above).',
            value: portal,
            onChange: togglePortal,
            disabled: false,
        },
    ];

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <div>
                    <h2 className="pp-section__title">Alert Behavior Settings</h2>
                    <p className="ss-section-desc">
                        Configure alert timeouts, interaction requirements, and the maximum alert lifetime.
                    </p>
                </div>
                {isDirty ? (
                    <div className="pp-section__actions">
                        <button className="dp-btn dp-btn--cancel" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="pp-section__body">
                {/* ──────────────────────────────────────────────────────────
                   Subsection: Interaction Requirements
                   ────────────────────────────────────────────────────────── */}
                <div className="ss-subsection">
                    <div className="ss-subsection__header">
                        <h3 className="ss-subsection__title">Interaction Requirements</h3>
                        <p className="ss-subsection__desc">
                            Control which staff actions are required on each alert.
                        </p>
                    </div>
                    <div className="ss-subsection__body">
                        {toggleRows.map((t) => (
                            <div
                                key={t.key}
                                className={`ss-toggle-row${t.disabled ? ' is-disabled' : ''}`}
                                role="switch"
                                aria-checked={t.value}
                                aria-disabled={t.disabled || undefined}
                                tabIndex={t.disabled ? -1 : 0}
                                onClick={t.disabled ? undefined : t.onChange}
                                onKeyDown={(e) => {
                                    if (!t.disabled && (e.key === 'Enter' || e.key === ' ')) {
                                        e.preventDefault();
                                        t.onChange();
                                    }
                                }}
                            >
                                <div className="ss-toggle-row__info">
                                    <h4 className="ss-toggle-row__title">{t.title}</h4>
                                    <p className="ss-toggle-row__desc">{t.desc}</p>
                                </div>
                                <div className="ss-toggle-row__control">
                                    <div className={`ss-toggle__track${t.value ? ' is-active' : ''}`} aria-hidden="true">
                                        <div className="ss-toggle__knob" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ──────────────────────────────────────────────────────────
                   Subsection: Alert Timing
                   ────────────────────────────────────────────────────────── */}
                <div className="ss-subsection">
                    <div className="ss-subsection__header">
                        <h3 className="ss-subsection__title">Alert Timing</h3>
                        <p className="ss-subsection__desc">
                            Set the maximum alert lifetime and the release / finish / clear timers that run within it.
                        </p>
                    </div>
                    <div className="ss-subsection__body">
                        {/* Max Alert Timeout (TTL) */}
                        <div className="ss-field" style={{ maxWidth: '200px' }}>
                            <label className="ss-field__label">Max Alert Timeout (minutes)</label>
                            <input
                                className="ss-number-input"
                                type="number"
                                min={60}
                                max={360}
                                step={5}
                                value={ttl}
                                onChange={handleTtlChange}
                                onBlur={handleTtlChange}
                            />
                            <p className="ss-field__hint">Range: 60 – 360 minutes</p>
                        </div>

                        {/* Release / Finish / Clear sliders — row on desktop, stacked on mobile */}
                        <div className="ss-range-row">
                            {/* Release Timeout slider */}
                            <div className="ss-range">
                                <div className="ss-range__header">
                                    <h4 className="ss-range__title">Release Timeout</h4>
                                </div>
                                <p className="ss-range__desc">
                                    Time to release alert after accepted with no device clear.
                                </p>
                                <div className="ss-range__track-wrap">
                                    <span
                                        className="ss-range__value"
                                        style={{ left: `calc(${releasePercent}% + ${10 - releasePercent * 0.175}px)` }}
                                    >
                                        {release} Min
                                    </span>
                                    <input
                                        className="ss-range__input"
                                        type="range"
                                        min={1}
                                        max={releaseMax}
                                        step={1}
                                        value={release}
                                        onChange={(e) => setRelease(parseInt(e.target.value))}
                                    />
                                </div>
                                <div className="ss-range__labels"><span>1 Min</span><span>{releaseMax} Min</span></div>
                            </div>

                            {/* Clear Timeout slider */}
                            <div className="ss-range">
                                <div className="ss-range__header">
                                    <h4 className="ss-range__title">Clear Timeout</h4>
                                </div>
                                <p className="ss-range__desc">
                                    Time to auto-clear alert after no contact from system.
                                </p>
                                <div className="ss-range__track-wrap">
                                    <span
                                        className="ss-range__value"
                                        style={{ left: `calc(${clearPercent}% + ${10 - clearPercent * 0.175}px)` }}
                                    >
                                        {clearTime} Min
                                    </span>
                                    <input
                                        className="ss-range__input"
                                        type="range"
                                        min={4}
                                        max={clearMax}
                                        step={1}
                                        value={clearTime}
                                        onChange={(e) => handleClearChange(e.target.value)}
                                    />
                                </div>
                                <div className="ss-range__labels"><span>4 Min</span><span>{clearMax} Min</span></div>
                            </div>

                            {/* Finish Timeout slider */}
                            <div className="ss-range">
                                <div className="ss-range__header">
                                    <h4 className="ss-range__title">Finish Timeout</h4>
                                </div>
                                <p className="ss-range__desc">
                                    Time to auto-finish alarm if stale after accept and clear.
                                </p>
                                <div className="ss-range__track-wrap">
                                    <span
                                        className="ss-range__value"
                                        style={{ left: `calc(${finishPercent}% + ${10 - finishPercent * 0.175}px)` }}
                                    >
                                        {finishTime} Min
                                    </span>
                                    <input
                                        className="ss-range__input"
                                        type="range"
                                        min={5}
                                        max={finishMax}
                                        step={1}
                                        value={finishTime}
                                        onChange={(e) => handleFinishChange(e.target.value)}
                                    />
                                </div>
                                <div className="ss-range__labels"><span>5 Min</span><span>{finishMax} Min</span></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ──────────────────────────────────────────────────────────
                   Subsection: Device Communication
                   ────────────────────────────────────────────────────────── */}
                <div className="ss-subsection">
                    <div className="ss-subsection__header">
                        <h3 className="ss-subsection__title">Device Communication</h3>
                        <p className="ss-subsection__desc">
                            Minutes without a device check-in before marking it Communication Lost.
                        </p>
                    </div>
                    <div className="ss-subsection__body">
                        <div className="ss-field-grid">
                            <div className="ss-field" style={{ maxWidth: '220px' }}>
                                <label className="ss-field__label">Area Device Timeout (minutes)</label>
                                <input
                                    className="ss-number-input"
                                    type="number"
                                    min={120}
                                    max={1440}
                                    step={1}
                                    value={areaDeviceTO}
                                    onChange={handleAreaDeviceTOChange}
                                    onBlur={handleAreaDeviceTOChange}
                                />
                                <p className="ss-field__hint">Devices attached to rooms/areas. Range: 120 – 1440 min (2 – 24 hrs).</p>
                            </div>
                            <div className="ss-field" style={{ maxWidth: '220px' }}>
                                <label className="ss-field__label">Resident Device Timeout (minutes)</label>
                                <input
                                    className="ss-number-input"
                                    type="number"
                                    min={120}
                                    max={1440}
                                    step={1}
                                    value={residentDeviceTO}
                                    onChange={handleResidentDeviceTOChange}
                                    onBlur={handleResidentDeviceTOChange}
                                />
                                <p className="ss-field__hint">Devices attached to residents. Range: 120 – 1440 min (2 – 24 hrs).</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CloudAlertSettingsTab;
